/**
 * 跑腿下单 — API 翻译层
 *
 * 翻译自 user/api/api-list.js 中的相关方法：
 *   delivery/*（配送单）、market/*（优惠券）、address/*、common/*、module/*、user/*、rider_enter/*、notice/*
 *
 * 阶段 4：扩展 mock 数据，加入用户、骑手、公告、区域接口。
 * 阶段 5：切换为真实 fetch 调用 Saleor GraphQL + dk_custom REST。
 */

import { httpPost, httpGet, http, BASE_URL, type ApiResponse } from "./client";

// ==============================================================================
// 类型定义
// ==============================================================================

export type Address = {
  id: number;
  name: string;
  mobile: string;
  province: string;
  city: string;
  district: string;
  detail: string;
  is_default?: boolean;
  location?: { lat: number; lng: number };
};

export type Coupon = {
  id: number;
  name: string;
  type: "VOUCHER" | "FULL" | "DISCOUNT";
  money?: number;
  discount?: number;
  min_money: number;
  expire_time_text: string;
};

export type CalculatePriceInfo = {
  base_fee: number;
  distance_fee: number;
  weight_fee: number;
  time_fee: number;
  tip_fee: number;
  coupon_discount: number;
  total_amount: number; // 扣券前原价（= base+distance+weight+time+tip）
  actual_amount: number; // = max(0, total_amount - coupon_discount)
  coupon_text?: string;
  discounts: number; // 历史兼容字段：= total_amount（扣券前）
  distance: number; // 米
  distance_meters?: number;
  is_night?: boolean;
};

export type DeliveryOrder = {
  id?: number;
  order_id?: number;
  order_no: string;
  status: number;
  status_text: string;
  amount?: number;
  // 阶段 11：支付拆分快照（后端 order.to_dict 返回）
  total_amount?: number;
  coupon_discount?: number;
  balance_deducted?: number;
  actual_amount?: number;
  pay_method?: string;
  pay_method_text?: string;
  pay_amount?: number;
  pay_url?: string; // 待付款订单的第三方收银台跳转 URL（支付宝 wap / 微信 h5）
  pay_channel?: string; // 待付款订单第三方支付渠道（ALIPAY/WECHAT）
  channel?: string; // 订单第三方支付渠道（原路退回用）
  need_pay?: boolean; // 是否需第三方支付（status=0 待付款）
  user_coupon_id?: number;
  module_name: string;
  module_type?: string;
  start_address: string;
  end_address: string;
  start_location?: { lat: number; lng: number };
  end_location?: { lat: number; lng: number };
  // 展开的单独坐标字段（后端真实接口返回的）
  start_lat?: number;
  start_lng?: number;
  end_lat?: number;
  end_lng?: number;
  tip_fee?: number;
  advance_fee?: number;
  weight_kg?: number;
  distance_meters?: number;
  rider?: {
    id?: number;
    name: string;
    real_name?: string;
    mobile: string;
    avatar?: string;
    location?: { lat: number; lng: number };
    // 骑手实时位置与评分（骑手端已接单 / 进行中返回）
    current_lat?: number;
    current_lng?: number;
    avg_rating?: number;
    credit_score?: number;
  };
  pickup_code?: string;
  receive_code?: string;
  picked_up_at?: string;
  accepted_at?: string;
  paid_at?: string;
  delivered_at?: string;
  cancelled_at?: string;
  customer_mobile?: string;
  create_time_text?: string;
  created_at?: string;
  remark?: string;
};

/** 订单实时追踪数据（GET /api/dk/rider/track，阶段 15） */
export type RiderTrackData = {
  order_id: number;
  order_no: string;
  status: number;
  status_text: string;
  module_type?: string;
  module_name: string;
  start_address: string;
  end_address: string;
  start_lat?: number;
  start_lng?: number;
  end_lat?: number;
  end_lng?: number;
  rider: {
    id: number;
    real_name: string;
    mobile: string;
    current_lat: number;
    current_lng: number;
    avg_rating?: number;
    total_orders?: number;
    distance_to_end_meters?: number;
    /** 距上次上报秒数 */
    location_age_seconds?: number | null;
    /** 超过 120s 未上报 → true */
    location_stale?: boolean;
  } | null;
  eta_minutes: number | null;
  /** 骑手最近轨迹点 [lng, lat, ts]（仅进行中订单） */
  track_path: Array<[number, number, number]>;
};

export type Notice = {
  id: number;
  title: string;
  content: string;
  create_time_text: string;
  is_read?: boolean;
};

export type Zone = {
  id: number;
  name: string;
  city?: string;
  letter?: string;
  center_lat?: number;
  center_lng?: number;
};

export type RiderApplication = {
  real_name: string;
  gender: 0 | 1;
  id_card_front: string;
  id_card_back: string;
  apply_remark: string;
};

export type RiderApplyStatus = {
  applied: boolean;
  rider_id?: number;
  real_name?: string;
  mobile?: string;
  gender?: number;
  status?: string;
  status_text?: string;
  reject_reason?: string;
  id_card_front?: string;
  id_card_back?: string;
  apply_remark?: string;
};

// ==============================================================================
// Mock 基础数据
// ==============================================================================

const MOCK_ADDRESSES: Address[] = [
  {
    id: 1,
    name: "张三",
    mobile: "138****1234",
    province: "北京市",
    city: "北京市",
    district: "朝阳区",
    detail: "建国路 88 号 SOHO 现代城 A 座 1501",
    is_default: true,
    location: { lat: 39.9087, lng: 116.4501 },
  },
  {
    id: 2,
    name: "李四",
    mobile: "139****5678",
    province: "北京市",
    city: "北京市",
    district: "海淀区",
    detail: "中关村大街 27 号",
    location: { lat: 39.9789, lng: 116.3168 },
  },
];

const MOCK_COUPONS: Coupon[] = [
  {
    id: 101,
    name: "新用户专享券",
    type: "VOUCHER",
    money: 5,
    min_money: 0,
    expire_time_text: "2026-12-31 到期",
  },
  {
    id: 102,
    name: "满 30 减 5",
    type: "FULL",
    money: 5,
    min_money: 30,
    expire_time_text: "2026-09-30 到期",
  },
  {
    id: 103,
    name: "跑腿 9 折",
    type: "DISCOUNT",
    discount: 9,
    min_money: 20,
    expire_time_text: "2026-08-31 到期",
  },
  {
    id: 104,
    name: "夜宵专享 8 折",
    type: "DISCOUNT",
    discount: 8,
    min_money: 50,
    expire_time_text: "2026-10-31 到期",
  },
];

const MOCK_NOTICES: Notice[] = [
  {
    id: 1,
    title: "🎉 平台 v2.0 上线",
    content:
      "尊敬的用户：\n\n本次更新带来全新视觉体验，包括：\n1. 全新首页布局\n2. 智能地址推荐\n3. 多重优惠券叠加\n4. 骑手位置实时显示\n\n感谢您一路的支持！",
    create_time_text: "2026-08-10",
    is_read: false,
  },
  {
    id: 2,
    title: "📢 关于雨雪天气服务费调整的通知",
    content:
      "雨雪天气配送费将会上浮，请用户提前规划下单时间。\n\n具体调整：\n- 小雨：基础费用 + 3 元\n- 中雨/小雪：基础费用 + 5 元\n- 大雨/大雪：基础费用 + 8 元",
    create_time_text: "2026-08-05",
    is_read: true,
  },
  {
    id: 3,
    title: "🛵 骑手招募计划",
    content:
      "诚邀有识之士加入我们的骑手团队，月入过万不是梦！\n\n福利：\n- 保底 6000 元/月\n- 高峰时段 +200% 配送费\n- 完善的社会保险\n- 灵活的上班时间",
    create_time_text: "2026-07-28",
    is_read: true,
  },
];

const MOCK_ZONES: Zone[] = [
  { id: 1, name: "北京", city: "北京", letter: "B" },
  { id: 2, name: "上海", city: "上海", letter: "S" },
  { id: 3, name: "广州", city: "广州", letter: "G" },
  { id: 4, name: "深圳", city: "深圳", letter: "S" },
  { id: 5, name: "杭州", city: "杭州", letter: "H" },
  { id: 6, name: "成都", city: "成都", letter: "C" },
  { id: 7, name: "南京", city: "南京", letter: "N" },
  { id: 8, name: "武汉", city: "武汉", letter: "W" },
];

// ==============================================================================
// Mock 状态
// ==============================================================================

let mockAddressStore: Address[] = [...MOCK_ADDRESSES];

function mockOk<T>(data: T): Promise<ApiResponse<T>> {
  return Promise.resolve({ code: 1, data });
}

/** 是否使用 mock（默认 false = 真实接口） */
const USE_MOCK = process.env.NEXT_PUBLIC_USE_MOCK === "true";

// ==============================================================================
// 地址接口（持久化到后端 UserAddress 表）
// ==============================================================================

/** 当前登录用户信息（新增地址页默认填充姓名/手机号） */
export type UserMe = {
  user_id: number;
  email: string;
  nickname: string;
  mobile: string;
  default_name: string;
  default_mobile: string;
};

export const userMe = () => wrap<UserMe>(httpGet<any>("/api/dk/user/me"));

/** 把后端 items[] 形态转成前端 Address[]（带 location 字段） */
function toAddress(it: any): Address {
  return {
    id: it.id,
    name: it.name,
    mobile: it.mobile,
    province: it.province || "",
    city: it.city || "",
    district: it.district || "",
    detail: it.detail,
    is_default: !!it.is_default,
    location: it.location || undefined,
  };
}

export const addressList = async (params?: { keyword?: string }): Promise<ApiResponse<Address[]>> => {
  const q = params?.keyword ? `?keyword=${encodeURIComponent(params.keyword)}` : "";
  const res = await wrap<{ items: any[] }>(httpGet<any>(`/api/dk/user/address/list${q}`));
  if (res.code !== 1) return { code: res.code, data: null as any, msg: res.msg };
  return { code: 1, data: (res.data?.items || []).map(toAddress), msg: res.msg };
};

/** 把前端 Address.location {lat,lng} 拍平成后端期望的顶层 lat/lng 字段 */
function flattenAddressBody(data: Partial<Address>): Record<string, any> {
  const body: Record<string, any> = { ...data };
  if (data.location) {
    body.lat = data.location.lat;
    body.lng = data.location.lng;
    delete body.location;
  }
  return body;
}

export const addressAdd = (data: Omit<Address, "id">) =>
  wrap<Address>(httpPost<any>("/api/dk/user/address/save", flattenAddressBody(data))).then((res) => {
    if (res.code === 1 && res.data) res.data = toAddress(res.data);
    return res as ApiResponse<Address>;
  });

export const addressEdit = (data: Address) =>
  wrap<Address>(httpPost<any>("/api/dk/user/address/save", flattenAddressBody(data))).then((res) => {
    if (res.code === 1 && res.data) res.data = toAddress(res.data);
    return res as ApiResponse<Address>;
  });

export const addressDelete = (params: { id: number }) =>
  wrap<{ id: number }>(httpPost<any>("/api/dk/user/address/delete", params));

export const addressDetail = async (params: { id: number }): Promise<ApiResponse<Address>> => {
  const res = await wrap<any>(httpGet<any>(`/api/dk/user/address/detail?id=${params.id}`));
  if (res.code === 1 && res.data) res.data = toAddress(res.data);
  return res as ApiResponse<Address>;
};

// ==============================================================================
// 配送单接口
// ==============================================================================

export const deliveryCalculate = (params: {
  module_id?: number;
  module_type_id?: number;
  module_type?: string;
  start_location?: { lat: number; lng: number };
  end_location?: { lat: number; lng: number };
  start_lat?: number;
  start_lng?: number;
  end_lat?: number;
  end_lng?: number;
  weight_kg?: number;
  weight?: number;
  tip_fee?: number;
  coupon_id?: number;
  user_coupon_id?: number;
}) => {
  if (USE_MOCK) {
    const base = 5;
    const startLoc = params.start_location || { lat: params.start_lat!, lng: params.start_lng! };
    const endLoc = params.end_location || { lat: params.end_lat!, lng: params.end_lng! };
    const distance = haversine(startLoc, endLoc);
    const distanceFee = +(distance / 1000 * 2).toFixed(2);
    const weightFee =
      (params.weight_kg || params.weight || 0) > 5
        ? ((params.weight_kg || params.weight || 0) - 5) * 0.5
        : 0;
    const tipFee = params.tip_fee || 0;
    const couponDiscount = params.coupon_id ? 5 : 0;
    const total = base + distanceFee + weightFee + tipFee;
    const actual = Math.max(0, total - couponDiscount);

    return mockOk<CalculatePriceInfo>({
      base_fee: base,
      distance_fee: +distanceFee.toFixed(2),
      weight_fee: +weightFee.toFixed(2),
      time_fee: 0,
      tip_fee: tipFee,
      coupon_discount: couponDiscount,
      total_amount: +total.toFixed(2),
      actual_amount: +actual.toFixed(2),
      discounts: +total.toFixed(2),
      distance: +distance.toFixed(0),
    });
  }
  // 真实接口：把前端字段映射到后端字段名
  return deliveryCalculateReal({
    module_type: params.module_type || "SEND",
    start_lat: params.start_lat ?? params.start_location!.lat,
    start_lng: params.start_lng ?? params.start_location!.lng,
    end_lat: params.end_lat ?? params.end_location!.lat,
    end_lng: params.end_lng ?? params.end_location!.lng,
    weight_kg: params.weight_kg ?? params.weight ?? 5,
    tip_fee: params.tip_fee ?? 0,
    coupon_id: params.coupon_id,
    user_coupon_id: params.user_coupon_id,
  });
};

export const deliveryCreateOrder = (data: any) => {
  if (USE_MOCK) {
    const order: DeliveryOrder = {
      id: Date.now(),
      order_no: "DT" + Date.now(),
      status: 1,
      status_text: "待接单",
      amount: data.actual_amount || 12,
      module_name: data.module_name || "帮我送",
      start_address: data.start_address || "",
      end_address: data.end_address || "",
      start_location: data.start_location,
      end_location: data.end_location,
      create_time_text: "刚刚",
    };
    return mockOk(order);
  }
  // 真实接口
  return deliveryCreateOrderReal({
    module_type: data.module_type || "SEND",
    start_address: data.start_address,
    start_lat: data.start_location?.lat ?? data.start_lat,
    start_lng: data.start_location?.lng ?? data.start_lng,
    end_address: data.end_address,
    end_lat: data.end_location?.lat ?? data.end_lat,
    end_lng: data.end_location?.lng ?? data.end_lng,
    // P5：位置简称（POI 名）+ 服务类型（特快/快车/半日达/拼送/RIDE 独享拼座）
    start_name: data.start_poi_name || "",
    end_name: data.end_poi_name || "",
    service_type: data.service_type || "",
    ride_mode: data.ride_mode || "",
    seats: data.seats ?? 1,
    weight_kg: data.weight ?? 5,
    tip_fee: data.tip_fee ?? 0,
    advance_fee: data.advance_fee ?? 0,
    coupon_id: data.coupon_id,
    user_coupon_id: data.user_coupon_id,
    use_balance: !!data.use_balance,
    pay_method: data.pay_method || "WECHAT",
    remark: data.remark ?? "",
    appoint_time_hours: data.appoint_time_hours ?? 0,
  });
};

export const deliveryList = (params?: { status?: number }) => {
  if (USE_MOCK) {
    const list: DeliveryOrder[] = [
      {
        id: 1001,
        order_no: "DT20260814001",
        status: 3,
        status_text: "已完成",
        amount: 15.5,
        module_name: "帮我送",
        start_address: "北京市朝阳区建国路 88 号",
        end_address: "北京市海淀区中关村大街 27 号",
        create_time_text: "2026-08-13 14:30",
      },
      {
        id: 1002,
        order_no: "DT20260812002",
        status: 2,
        status_text: "进行中",
        amount: 22.0,
        module_name: "帮我取",
        start_address: "北京市东城区王府井大街 1 号",
        end_address: "北京市西城区西单北大街 88 号",
        rider: {
          name: "王骑手",
          mobile: "138****0001",
          location: { lat: 39.9188, lng: 116.4101 },
        },
        create_time_text: "2026-08-13 10:15",
      },
    ];
    return mockOk(list);
  }
  // 真实接口
  return deliveryListReal(params);
};

export const deliveryDetail = (params: { id: number }) => {
  if (USE_MOCK) {
    return mockOk({
      id: params.id,
      order_no: "DT" + params.id,
      status: 2,
      status_text: "进行中",
      amount: 18.5,
      module_name: "帮我送",
      start_address: "北京市朝阳区建国路 88 号 SOHO 现代城",
      end_address: "北京市海淀区中关村大街 27 号",
      rider: {
        name: "王骑手",
        mobile: "138****0001",
        avatar: "",
        location: { lat: 39.9188, lng: 116.4101 },
      },
      create_time_text: "2026-08-14 09:30",
    });
  }
  // 真实接口
  return deliveryDetailReal(params);
};

export const deliveryCancel = (params: { id: number; reason?: string }) => {
  if (USE_MOCK) return mockOk({ id: params.id });
  return deliveryCancelReal(params);
};

export const deliveryRefund = (params: { id: number; reason?: string }) => {
  if (USE_MOCK) return mockOk({ id: params.id });
  return deliveryRefundReal(params);
};

export const deliveryCouponsUsable = (params: {
  module_type?: string;
  start_lat?: number;
  start_lng?: number;
  end_lat?: number;
  end_lng?: number;
  start_location?: { lat: number; lng: number };
  end_location?: { lat: number; lng: number };
  weight_kg?: number;
  weight?: number;
  tip_fee?: number;
}) => {
  if (USE_MOCK) {
    // mock：按 6 条假券兜底
    const mockItems: UsableCouponItem[] = [
      {
        user_coupon_id: 1,
        coupon_id: 1,
        title: "满 30 减 5",
        ctype: "FULL",
        amount: 5,
        min_amount: 30,
        is_new_user_only: false,
        expire_at_text: "2026-12-31",
        coupon_discount: 5,
        final_amount: 0,
        can_use: true,
        can_use_reason: "",
      },
    ];
    return mockOk({
      total_amount: 35,
      distance_meters: 0,
      recommend_user_coupon_id: 1,
      items: mockItems,
    });
  }
  return deliveryCouponsUsableReal({
    module_type: params.module_type || "SEND",
    start_lat: params.start_lat ?? params.start_location?.lat,
    start_lng: params.start_lng ?? params.start_location?.lng,
    end_lat: params.end_lat ?? params.end_location?.lat,
    end_lng: params.end_lng ?? params.end_location?.lng,
    weight_kg: params.weight_kg ?? params.weight ?? 5,
    tip_fee: params.tip_fee ?? 0,
  });
};

// ==============================================================================
// 优惠券接口（阶段 10 切真实；USE_MOCK=true 才回退 mock）
// ==============================================================================

export const marketGetMyCoupons = (params?: {
  status?: "UNUSED" | "USED" | "EXPIRED";
}) =>
  USE_MOCK
    ? mockOk<Coupon[]>(MOCK_COUPONS as any)
    : (couponMineReal(params) as any);
export const marketGetUsableCoupons = () =>
  USE_MOCK
    ? mockOk<Coupon[]>(MOCK_COUPONS as any)
    : (couponListReal() as any);
export const marketTakeCoupon = (params: { coupon_id: number }) =>
  USE_MOCK
    ? mockOk({ id: params.coupon_id })
    : (couponTakeReal({ coupon_id: params.coupon_id }) as any);
export const marketGetCoupons = () =>
  USE_MOCK
    ? mockOk<Coupon[]>(MOCK_COUPONS as any)
    : (couponListReal() as any);
export const marketTakeMultiCoupon = (params: { coupon_ids: number[] }) =>
  USE_MOCK
    ? mockOk({ ids: params.coupon_ids })
    : (couponTakeReal({ coupon_ids: params.coupon_ids }) as any);

// ==============================================================================
// 公告接口（阶段 10 切真实）
// ==============================================================================

export const noticeList = () => (USE_MOCK ? mockOk(MOCK_NOTICES) : noticeListReal());
export const noticeDetail = (params: { id: number }) =>
  USE_MOCK
    ? mockOk(MOCK_NOTICES.find((n) => n.id === params.id) || MOCK_NOTICES[0])
    : noticeDetailReal(params);

// ==============================================================================
// 区域接口（阶段 10 切真实）
// ==============================================================================

export const zoneList = (params?: { city?: string; letter?: string }) =>
  USE_MOCK ? mockOk(MOCK_ZONES) : zoneListReal(params);
export const zoneDetail = (params: { id: number }) =>
  USE_MOCK
    ? mockOk(MOCK_ZONES.find((z) => z.id === params.id) || MOCK_ZONES[0])
    : zoneDetailReal(params);

// ==============================================================================
// 模块 / 公共 / 骑手入驻接口
// ==============================================================================

export const moduleList = () =>
  mockOk([
    { id: 1, name: "帮我送", type: "SEND" },
    { id: 2, name: "帮我取", type: "TAKE" },
    { id: 3, name: "帮我买", type: "BUY" },
    { id: 4, name: "1V1", type: "1V1" },
    { id: 5, name: "万能服务", type: "WAN" },
  ]);

export const moduleRule = (params: { module_id: number }) =>
  mockOk({
    title: "计价规则",
    rules: [
      "基础费用 5 元",
      "距离费 2 元/公里",
      "5 公斤内免重量费，超出部分 0.5 元/公斤",
      "夜间服务（22:00-06:00）加收 5 元",
      "小费自愿，最低 1 元",
    ],
  });

export const commonIndex = () =>
  mockOk({ banner: [], zone: { id: 1, name: "北京" }, modules: [], notice: [] });

export const riderEnterApply = (data: RiderApplication) => {
  if (USE_MOCK) return mockOk({ id: Date.now(), ...data, status_text: "审核中" });
  return riderApplyReal(data);
};

export const riderApplyStatus = () => {
  if (USE_MOCK) return mockOk({ applied: false });
  return wrap<RiderApplyStatus>(httpGet<any>("/api/dk/rider/apply/status"));
};

export const uploadImage = (image: string, folder = "rider/idcard") => {
  if (USE_MOCK) return mockOk({ url: image, path: "" });
  return wrap<{ url: string; path: string }>(httpPost<any>("/api/dk/upload/image", { image, folder }));
};

// ==============================================================================
// 真实接口（阶段 5 用）
// ==============================================================================

/** 适配后端 ok/error 响应 → ApiResponse 格式 */
async function wrap<T>(p: Promise<ApiResponse<T>>): Promise<ApiResponse<T>> {
  try {
    const r = await p;
    if (r.code === 1) return r;
    return { code: r.code, data: null as T, msg: r.msg || "请求失败" };
  } catch (e: any) {
    // ApiError 有 code 属性
    if (e?.code !== undefined) {
      return { code: e.code, data: null as T, msg: e.message || "请求失败" };
    }
    return { code: -1, data: null as T, msg: String(e?.message || e) };
  }
}

export const deliveryCalculateReal = async (
  params: any,
): Promise<ApiResponse<CalculatePriceInfo>> => {
  const r = await wrap<any>(
    httpPost<any>("/api/dk/delivery/calculate", params),
  );
  if (r.code !== 1) return { code: r.code, data: null as any, msg: r.msg };
  const d = r.data || {};
  const distance = +(d.distance_meters ?? d.distance ?? 0);
  return {
    code: 1,
    msg: r.msg,
    data: {
      base_fee: +(d.base_fee ?? 0),
      distance_fee: +(d.distance_fee ?? 0),
      weight_fee: +(d.weight_fee ?? 0),
      time_fee: +(d.time_fee ?? 0),
      tip_fee: +(d.tip_fee ?? 0),
      coupon_discount: +(d.coupon_discount ?? 0),
      total_amount: +(d.total_amount ?? d.discounts ?? 0),
      actual_amount: +(d.actual_amount ?? 0),
      coupon_text: d.coupon_text,
      discounts: +(d.total_amount ?? d.discounts ?? 0),
      distance,
      distance_meters: distance,
      is_night: !!d.is_night,
    },
  };
};

export const deliveryCouponsUsableReal = (
  params: any,
): Promise<
  ApiResponse<{
    total_amount: number;
    distance_meters: number;
    recommend_user_coupon_id: number | null;
    items: UsableCouponItem[];
  }>
> =>
  wrap(
    httpPost<any>("/api/dk/delivery/coupons-usable", params),
  ) as any;

export type UsableCouponItem = {
  user_coupon_id: number;
  coupon_id: number;
  title: string;
  ctype: "FULL" | "DISCOUNT" | "FIRST" | "NEW";
  amount: number;
  min_amount: number;
  is_new_user_only: boolean;
  expire_at_text: string;
  coupon_discount: number;
  final_amount: number;
  can_use: boolean;
  can_use_reason: string;
};

export const deliveryCreateOrderReal = (params: any) =>
  wrap(httpPost<DeliveryOrder>("/api/dk/delivery/create", params));

export const deliveryListReal = async (params?: { status?: number }): Promise<ApiResponse<DeliveryOrder[]>> => {
  const url = params?.status !== undefined
    ? `/api/dk/delivery/list?status=${params.status}`
    : "/api/dk/delivery/list";
  const r = await wrap<any>(httpGet<any>(url));
  if (r.code === 1) {
    const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items || []);
    // 后端 to_dict 返回 order_id/actual_amount/created_at(ISO)，归一化为页面用的
    // id/amount/create_time_text（否则列表 key={order.id} 为 undefined、详情跳转 /undefined）
    const items: DeliveryOrder[] = raw.map((o) => ({
      ...o,
      id: o.id ?? o.order_id,
      amount: o.amount ?? o.actual_amount ?? o.total_amount,
      create_time_text:
        o.create_time_text ||
        (o.created_at ? new Date(o.created_at).toLocaleString("zh-CN", { hour12: false }) : ""),
    }));
    return { code: 1, data: items, msg: r.msg };
  }
  return { code: r.code, data: null as any, msg: r.msg };
};

export const deliveryDetailReal = async (params: { id: number }): Promise<ApiResponse<DeliveryOrder>> => {
  const r = await wrap<any>(httpGet<any>(`/api/dk/delivery/${params.id}/detail`));
  if (r.code === 1 && r.data) {
    // 后端 to_dict 返回 order_id/actual_amount/created_at(ISO)，归一化为页面用的
    // id/amount/create_time_text（否则详情金额不显示）
    r.data.id = r.data.id ?? r.data.order_id;
    r.data.amount = r.data.amount ?? r.data.actual_amount ?? r.data.total_amount;
    r.data.create_time_text =
      r.data.create_time_text ||
      (r.data.created_at
        ? new Date(r.data.created_at).toLocaleString("zh-CN", { hour12: false })
        : "");
  }
  return r as ApiResponse<DeliveryOrder>;
};

export const deliveryCancelReal = (params: { id: number; reason?: string }) =>
  wrap(httpPost<any>(`/api/dk/delivery/${params.id}/cancel`, { reason: params.reason }));

export const deliveryRefundReal = (params: { id: number; reason?: string }) =>
  wrap(httpPost<any>(`/api/dk/delivery/${params.id}/refund`, { reason: params.reason }));

export const riderTrackReal = (params: { order_id: number }) =>
  wrap(httpGet<RiderTrackData>(`/api/dk/rider/track?order_id=${params.order_id}`));

export const riderApplyReal = (params: RiderApplication) =>
  wrap(httpPost<any>("/api/dk/rider/apply", params));

export const noticeListReal = async (): Promise<ApiResponse<Notice[]>> => {
  const r = await wrap<{ items: any[] }>(httpGet<{ items: any[] }>("/api/dk/notice/list"));
  if (r.code !== 1) return { code: r.code, data: null as any, msg: r.msg };
  const mapped: Notice[] = (r.data?.items || []).map((n) => ({
    id: n.id,
    title: n.title,
    content: n.content ?? "",
    create_time_text: n.start_at_text || n.publish_at_text || n.created_at_text || "",
    is_read: false,
  }));
  return { code: 1, data: mapped, msg: r.msg };
};

// 阶段 10：优惠券 3 个（真实接口）
export type CouponPool = {
  id: number;
  title: string;
  ctype: "FULL" | "DISCOUNT" | "FIRST" | "NEW";
  amount: number;
  discount?: number;
  min_amount: number;
  start_at_text?: string;
  expire_at_text?: string;
  left_stock: number;
  total_stock: number;
  is_new_user_only: boolean;
};
export type UserCouponItem = {
  id: number;
  coupon_id: number;
  title: string;
  ctype: "FULL" | "DISCOUNT" | "FIRST" | "NEW";
  status: "UNUSED" | "USED" | "EXPIRED";
  amount: number;
  discount?: number;
  min_amount: number;
  expire_at_text?: string;
  received_at_text?: string;
};

export const couponListReal = async (): Promise<ApiResponse<CouponPool[]>> => {
  const r = await wrap<{ items: any[] }>(httpGet<{ items: any[] }>("/api/dk/coupon/list"));
  if (r.code !== 1) return { code: r.code, data: null as any, msg: r.msg };
  const items: CouponPool[] = (r.data?.items || []).map((c) => ({
    id: c.id,
    title: c.title,
    ctype: c.ctype,
    amount: +(c.amount ?? 0),
    discount: c.discount ? +c.discount : undefined,
    min_amount: +(c.min_amount ?? 0),
    start_at_text: c.start_at_text,
    expire_at_text: c.expire_at_text,
    left_stock: c.left_stock ?? 0,
    total_stock: c.total_stock ?? 0,
    is_new_user_only: !!c.is_new_user_only,
  }));
  return { code: 1, data: items, msg: r.msg };
};

export const couponMineReal = async (params?: {
  status?: "UNUSED" | "USED" | "EXPIRED";
}): Promise<ApiResponse<UserCouponItem[]>> => {
  const q = params?.status ? `?status=${params.status}` : "";
  const r = await wrap<{ items: any[] }>(
    httpGet<{ items: any[] }>(`/api/dk/coupon/mine${q}`),
  );
  if (r.code !== 1) return { code: r.code, data: null as any, msg: r.msg };
  const items: UserCouponItem[] = (r.data?.items || []).map((uc) => ({
    id: uc.id,
    coupon_id: uc.coupon_id,
    title: uc.title,
    ctype: uc.ctype,
    status: uc.status,
    amount: +(uc.amount ?? 0),
    discount: uc.discount ? +uc.discount : undefined,
    min_amount: +(uc.min_amount ?? 0),
    expire_at_text: uc.expire_at_text,
    received_at_text: uc.received_at_text,
  }));
  return { code: 1, data: items, msg: r.msg };
};

export const couponTakeReal = (params: {
  coupon_id?: number;
  coupon_ids?: number[];
}) =>
  wrap(
    httpPost<{ taken: UserCouponItem[] }>(
      "/api/dk/coupon/take",
      params as any,
    ),
  );

// 阶段 10：公告详情（真实接口）
export const noticeDetailReal = async (params: {
  id: number;
}): Promise<ApiResponse<Notice>> => {
  const r = await wrap<any>(httpGet<any>(`/api/dk/notice/${params.id}/detail`));
  if (r.code !== 1) return { code: r.code, data: null as any, msg: r.msg };
  const n = r.data || {};
  return {
    code: 1,
    data: {
      id: n.id,
      title: n.title,
      content: n.content ?? "",
      create_time_text:
        n.start_at_text || n.publish_at_text || n.created_at_text || "",
      is_read: false,
    },
  };
};

// 阶段 10：区域 2 个（真实接口）
export const zoneListReal = async (params?: {
  city?: string;
  letter?: string;
}): Promise<ApiResponse<Zone[]>> => {
  const qs = new URLSearchParams();
  if (params?.city) qs.set("city", params.city);
  if (params?.letter) qs.set("letter", params.letter);
  const q = qs.toString() ? `?${qs.toString()}` : "";
  const r = await wrap<{ items: any[] }>(httpGet<{ items: any[] }>(`/api/dk/zone/list${q}`));
  if (r.code !== 1) return { code: r.code, data: null as any, msg: r.msg };
  const items: Zone[] = (r.data?.items || []).map((z) => ({
    id: z.id,
    name: z.name,
    city: z.city,
    letter: z.letter,
    center_lat: z.center_lat,
    center_lng: z.center_lng,
  }));
  return { code: 1, data: items, msg: r.msg };
};

export const zoneDetailReal = (params: { id: number }) =>
  wrap(httpGet<Zone>(`/api/dk/zone/${params.id}/detail`));

// 阶段 10：钱包 / 充值 3 个
export type WalletInfo = {
  wallet: { id: number; balance: number; frozen: number };
  recent_recharges: RechargeItem[];
};
export type RechargeItem = {
  id: number;
  order_no: string;
  amount: number;
  bonus_amount: number;
  pay_amount: number;
  status: number;
  status_text?: string;
  pay_method: string;
  created_at_text?: string;
  pay_at_text?: string;
};

export const walletInfoReal = (): Promise<ApiResponse<WalletInfo>> =>
  wrap(httpGet<WalletInfo>("/api/dk/wallet/info"));

// 阶段 20：支付方式可用性（后台开关决定前端是否展示支付宝/微信/余额）
export type PaymentMethods = {
  alipay: boolean;
  wechat: boolean;
  balance: boolean;
};

export const paymentMethodsReal = (): Promise<ApiResponse<PaymentMethods>> =>
  wrap(httpGet<PaymentMethods>("/api/dk/payment/methods"));

export const rechargeCreateReal = (params: {
  amount: number;
  bonus_amount?: number;
  pay_method?: "WECHAT" | "ALIPAY" | "BALANCE";
  test?: boolean;
}) =>
  wrap(
    httpPost<any>("/api/dk/recharge/create", {
      amount: params.amount,
      bonus_amount: params.bonus_amount ?? 0,
      pay_method: params.pay_method ?? "ALIPAY",
      test: params.test ?? true,
    }),
  );

// 阶段 17：充值单支付结果（从支付宝收银台回来后轮询）
export type RechargeStatus = {
  order_no: string;
  status: number;
  status_text?: string;
  channel: string;
  trade_no: string;
  amount: number;
  bonus_amount: number;
  paid: boolean;
  balance: number;
};

export const rechargeStatusReal = (orderNo: string) =>
  wrap(httpGet<RechargeStatus>(`/api/dk/recharge/${orderNo}/status`));

export const rechargeListReal = async (): Promise<ApiResponse<RechargeItem[]>> => {
  const r = await wrap<{ items: any[] }>(
    httpGet<{ items: any[] }>("/api/dk/recharge/list"),
  );
  if (r.code !== 1) return { code: r.code, data: null as any, msg: r.msg };
  return { code: 1, data: r.data?.items ?? [], msg: r.msg };
};

// 阶段 10：短信 2 个
export const smsSendReal = (params: {
  mobile: string;
  scene?:
    | "USER_LOGIN"
    | "USER_REG"
    | "RIDER_REG"
    | "RESET_PWD"
    | "BIND_MOBILE";
}) => wrap(httpPost<any>("/api/dk/sms/send", params));

export const smsVerifyReal = (params: {
  mobile: string;
  code: string;
  scene?: string;
}) => wrap(httpPost<any>("/api/dk/sms/verify", params));

// 用户手机验证码注册（成功返回 email，前端随即调 userLoginReal 自动登录）
export const userRegisterReal = (params: {
  mobile: string;
  password: string;
  sms_code: string;
}) => wrap<{ user_id: number; email: string; message: string }>(
  httpPost<any>("/api/dk/user/register", params),
);

// 用户手机验证码找回密码
export const userResetPwdReal = (params: {
  mobile: string;
  password: string;
  sms_code: string;
}) => wrap<{ ok: boolean; message: string }>(
  httpPost<any>("/api/dk/user/reset-password", params),
);

// 附近在线骑手（首页地图）
export const userNearbyRidersReal = (params: { lat: number; lng: number }) =>
  wrap<{ count: number; riders: { lat: number; lng: number }[] }>(
    httpPost<any>("/api/dk/user/nearby-riders", params),
  );

// 用户统一登录（手机号或邮箱 + 密码，后端自动识别手机号并翻译为账号）
export const userLoginReal = async (params: { account: string; password: string }): Promise<ApiResponse<{ token: string; refreshToken?: string; user: any }>> => {
  try {
    const r = await httpPost<any>("/api/dk/user/login", params);
    return { code: 1, data: r.data, msg: r.msg || "登录成功" };
  } catch (e: any) {
    return { code: e?.code ?? -1, data: null as any, msg: e?.message || String(e) };
  }
};

// 手机号脱敏显示：138****1234
export const maskMobile = (m: string): string =>
  m && m.length >= 11 ? `${m.slice(0, 3)}****${m.slice(-4)}` : (m || "");

// ==============================================================================
// 阶段 12b+：管理员 API（骑手审核 + 订单分配）
// ==============================================================================

export type AdminRiderItem = {
  rider_id: number;
  real_name: string;
  mobile: string;
  gender: number;
  gender_text: string;
  status: "PENDING" | "APPROVED" | "REJECTED" | "DISABLED";
  status_text: string;
  reject_reason: string;
  work_status: number;
  email: string;
  total_orders: number;
  avg_rating: number;
  wallet_balance: string;
  credit_score: number;
  created_at?: string;
  updated_at?: string;
};

export type AdminRiderListResult = {
  total: number;
  page: number;
  page_size: number;
  items: AdminRiderItem[];
};

export type AdminOrderListResult = {
  total: number;
  page: number;
  page_size: number;
  items: DeliveryOrder[];
};

export type AdminRiderReviewResult = {
  rider_id: number;
  status: string;
  status_text: string;
  login_email?: string;
  login_mobile?: string;
  wallet_id?: number;
  wallet_balance?: string;
  credit_score?: number;
  message?: string;
};

export const adminRiderList = (params: {
  status?: string;
  page?: number;
  page_size?: number;
}) => wrapStaff<AdminRiderListResult>(
  httpStaffGet<AdminRiderListResult>("/api/dk/admin/rider/list", params as any),
);

export const adminOrderList = (params: {
  status?: number | string;
  page?: number;
  page_size?: number;
}) => wrapStaff<AdminOrderListResult>(
  httpStaffGet<AdminOrderListResult>("/api/dk/admin/order/list", params as any),
);

export const adminRiderReview = (rider_id: number, params: {
  action: "APPROVED" | "REJECTED" | "DISABLED";
  reject_reason?: string;
}) => wrapStaff<AdminRiderReviewResult>(
  httpStaffPost<AdminRiderReviewResult>(
    `/api/dk/admin/rider/${rider_id}/review`, params,
  ),
);

export const adminOrderAssign = (order_id: number, params: { rider_id: number }) =>
  wrapStaff<{ order_id: number; rider_id: number }>(
    httpStaffPost<any>(`/api/dk/admin/order/${order_id}/assign`, params),
  );

// 管理员权限的 HTTP 包装（读取管理员专用 token）
function getAdminToken(): string {
  if (typeof window === "undefined") return "";
  return localStorage.getItem("xiadan_admin_token") || "";
}

function httpStaff<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const token = getAdminToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };
  return http<T>(path, { ...options, headers });
}

function httpStaffGet<T>(path: string, params?: Record<string, string | number | boolean | undefined>): Promise<ApiResponse<T>> {
  let url = path;
  if (params) {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== "")
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join("&");
    if (qs) url += (path.includes("?") ? "&" : "?") + qs;
  }
  return httpStaff<T>(url, { method: "GET" });
}

function httpStaffPost<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
  return httpStaff<T>(path, {
    method: "POST",
    body: body ? JSON.stringify(body) : undefined,
  });
}

function wrapStaff<T>(p: Promise<ApiResponse<T>>): Promise<ApiResponse<T>> {
  return wrap<T>(p);
}

// ==============================================================================
// 工具函数
// ==============================================================================

function haversine(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(s));
}
